import { useState, useEffect } from 'react';
import { useOrgTimezone, formatDateInTz } from '../../utils/timezone';
import { useParams, useNavigate } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import { Card, CardContent } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import equipmentService from '../../services/equipment';
import type { Equipment, EquipmentAttachment } from '../../types/equipment';
import { Wrench, Link, Unlink, CheckCircle, Clock } from 'lucide-react';

// ─── Component ───────────────────────────────────────────────────────────────

export default function AttachImplement() {
  const timezone = useOrgTimezone();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const eqId = parseInt(id!);

  const [equipment, setEquipment]         = useState<Equipment | null>(null);
  const [implements_, setImplements]      = useState<Equipment[]>([]);
  const [currentAttachments, setCurrentAttachments] = useState<EquipmentAttachment[]>([]);
  const [history, setHistory]             = useState<EquipmentAttachment[]>([]);
  const [loading, setLoading]             = useState(true);
  const [attaching, setAttaching]         = useState<number | null>(null);
  const [detaching, setDetaching]         = useState<number | null>(null);
  const [showHistory, setShowHistory]     = useState(false);

  useEffect(() => {
    Promise.all([
      equipmentService.getById(eqId),
      equipmentService.list({ is_implement: true }),
      equipmentService.getAttachments(eqId),
      equipmentService.getAttachmentHistory(eqId),
    ]).then(([eqRes, implRes, attRes, histRes]) => {
      if (eqRes.success  && eqRes.data)  setEquipment(eqRes.data);
      if (implRes.success && implRes.data) setImplements(implRes.data);
      if (attRes.success  && attRes.data)  setCurrentAttachments(attRes.data.filter((a: EquipmentAttachment) => !a.detached_at));
      if (histRes.success && histRes.data) setHistory(histRes.data.filter((a: EquipmentAttachment) => a.detached_at));
    }).catch(() => {}).finally(() => setLoading(false));
  }, [eqId]);

  const currentIds = new Set(currentAttachments.map(a => a.implement_id));

  async function handleAttach(implementId: number) {
    setAttaching(implementId);
    try {
      const res = await equipmentService.attach(eqId, implementId);
      if (res.success && res.data) {
        setCurrentAttachments(prev => [...prev, res.data!]);
      }
    } catch {
      // silent
    } finally {
      setAttaching(null);
    }
  }

  async function handleDetach(attachmentId: number) {
    setDetaching(attachmentId);
    try {
      await equipmentService.detach(attachmentId);
      setCurrentAttachments(prev => {
        const detached = prev.find(a => a.attachment_id === attachmentId);
        if (detached) setHistory(h => [{ ...detached, detached_at: new Date().toISOString() }, ...h]);
        return prev.filter(a => a.attachment_id !== attachmentId);
      });
    } catch {
      // silent
    } finally {
      setDetaching(null);
    }
  }

  if (loading || !equipment) {
    return (
      <div className="min-h-screen bg-neutral-50">
        <PageHeader title="Attach Implement" />
        <div className="max-w-xl mx-auto px-4 py-6 space-y-3">
          {[0, 1, 2].map(i => <div key={i} className="h-16 bg-white rounded-xl animate-pulse border border-neutral-100" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50">
      <PageHeader title="Attach Implement" subtitle={equipment.name} />

      <div className="max-w-xl mx-auto px-4 py-6 space-y-4">

        {/* Currently attached */}
        {currentAttachments.length > 0 && (
          <Card>
            <CardContent className="pt-4">
              <p className="text-xs font-medium text-neutral-400 uppercase tracking-wide mb-3">Currently Attached</p>
              <ul className="space-y-2">
                {currentAttachments.map(att => (
                  <li key={att.attachment_id} className="flex items-center justify-between bg-green-50 border border-green-100 rounded-lg px-3 py-2.5">
                    <div className="flex items-center gap-2">
                      <Wrench className="h-4 w-4 text-green-600" />
                      <div>
                        <p className="text-sm font-medium text-neutral-800">{att.implement_name}</p>
                        <p className="text-xs text-neutral-500 capitalize">{att.implement_type?.replace(/_/g, ' ')}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleDetach(att.attachment_id)}
                      disabled={detaching === att.attachment_id}
                      className="flex items-center gap-1 text-xs text-red-500 hover:text-red-700 font-medium disabled:opacity-50"
                    >
                      <Unlink className="h-3.5 w-3.5" />
                      Detach
                    </button>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        {/* Available implements */}
        <Card>
          <CardContent className="pt-4">
            <p className="text-xs font-medium text-neutral-400 uppercase tracking-wide mb-3">Available Implements</p>
            {implements_.length === 0 ? (
              <div className="text-center py-6">
                <Wrench className="h-8 w-8 text-neutral-200 mx-auto mb-2" />
                <p className="text-sm text-neutral-500">No implements found</p>
                <p className="text-xs text-neutral-400 mt-1">Create implements from the equipment list first.</p>
              </div>
            ) : (
              <ul className="space-y-2">
                {implements_.map(impl => {
                  const isAttached = currentIds.has(impl.id);
                  return (
                    <li
                      key={impl.id}
                      className={`flex items-center justify-between rounded-lg px-3 py-2.5 border ${
                        isAttached
                          ? 'bg-green-50 border-green-100 opacity-70'
                          : 'bg-white border-neutral-100'
                      }`}
                    >
                      <div>
                        <p className="text-sm font-medium text-neutral-800">{impl.name}</p>
                        <p className="text-xs text-neutral-500 capitalize">{impl.equipment_type.replace(/_/g, ' ')}</p>
                        {impl.meter_type !== 'none' && (
                          <p className="text-xs text-neutral-400">
                            {impl.meter_type === 'hours' || impl.meter_type === 'both'
                              ? `${impl.current_hours ?? 0} hrs` : ''}
                            {impl.meter_type === 'both' ? ' · ' : ''}
                            {impl.meter_type === 'km' || impl.meter_type === 'both'
                              ? `${impl.current_km ?? 0} km` : ''}
                          </p>
                        )}
                      </div>
                      {isAttached ? (
                        <span className="flex items-center gap-1 text-xs text-green-600 font-medium">
                          <CheckCircle className="h-3.5 w-3.5" />
                          Attached
                        </span>
                      ) : (
                        <Button
                          size="sm"
                          isLoading={attaching === impl.id}
                          onClick={() => handleAttach(impl.id)}
                          disabled={impl.status !== 'active'}
                        >
                          <Link className="h-3.5 w-3.5 mr-1" />
                          Attach
                        </Button>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </CardContent>
        </Card>

        {/* History toggle */}
        {history.length > 0 && (
          <div>
            <button
              onClick={() => setShowHistory(v => !v)}
              className="flex items-center gap-1.5 text-sm text-neutral-500 hover:text-neutral-700 font-medium"
            >
              <Clock className="h-4 w-4" />
              {showHistory ? 'Hide' : 'Show'} attachment history ({history.length})
            </button>

            {showHistory && (
              <ul className="mt-3 space-y-2">
                {history.map(att => (
                  <li key={att.attachment_id} className="bg-white border border-neutral-100 rounded-lg px-3 py-2.5 text-sm">
                    <p className="font-medium text-neutral-600">{att.implement_name}</p>
                    <p className="text-xs text-neutral-400">
                      Attached {formatDateInTz(att.attached_at, timezone)}
                      {att.detached_at && ` → Detached ${formatDateInTz(att.detached_at, timezone)}`}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        <Button variant="outline" className="w-full" onClick={() => navigate(`/equipment/${eqId}`)}>
          Back to {equipment.name}
        </Button>

      </div>
    </div>
  );
}
